import React, { FC, useMemo } from 'react';
import * as BizCharts from 'bizcharts';
import '../charts/chartTheme';
import { truncateCategoryLabel } from '../charts/chartUtils';
import {
	SECTION_COMPARISON_BUCKET_COLORS,
	WalkthroughSectionRow,
} from '../../utils/WalkthroughComparisonUtils';
import { buildGroupedComparisonTooltipHtml } from '../../utils/AnalyticsComparisonTooltipUtils';
import { floatToPercentageString } from '../../utils/DataFormatterUtils';
import useElementSize from '../../hooks/useElementSize';
import { cn } from '../../utils/ClassNamesUtils';

const { Chart, Coordinate, Axis, Geom, Legend, Tooltip } = BizCharts;

const AXIS_LINE_COLOR = '#D9D9D9';
const AXIS_LABEL_COLOR = 'rgba(0,0,0,0.45)';

export const SECTION_BAR_BAND_HEIGHT_PX = 26;

const SECTION_BAR_MAX_THICKNESS_PX = 32;
const DEFAULT_COLUMN_WIDTH_RATIO = 0.5;

const CHART_VERTICAL_CHROME_PX = 72;

const CATEGORY_AXIS_TITLE_OFFSET_PX = 170;
const CHART_PADDING: [number, number, number, number] = [16, 76, 48, 190];

const CATEGORY_LABEL_MAX_CHARS = 19;

const AXIS_LABEL_STYLE = {
	fontSize: 12,
	fontFamily: 'Roboto',
	fill: AXIS_LABEL_COLOR,
} as const;

const AXIS_TITLE_STYLE = { fontSize: 12, fill: AXIS_LABEL_COLOR } as const;

const CROSSHAIRS_CONFIG = {
	type: 'x' as const,
	line: {
		style: {
			stroke: 'rgba(85, 85, 85, 0.8)',
			lineWidth: 1,
			lineDash: [4, 4] as [number, number],
		},
	},
};

const UNSCORED_BAR_COLOR = '#B37FEB';

const truncateSectionLabel = (rawLabel): string =>
	truncateCategoryLabel(rawLabel, CATEGORY_LABEL_MAX_CHARS);

export const getSectionChartHeight = (rowCount: number): number =>
	rowCount * SECTION_BAR_BAND_HEIGHT_PX + CHART_VERTICAL_CHROME_PX;

/**
 * Walkthroughs often have only two or three scored sections. The compact band height would leave
 * that chart stranded in a fraction of the card, so let the bands grow into whatever height the
 * card gives us; the bar thickness stays capped so the bars never balloon. Section counts that
 * need more room than that keep the compact height and scroll.
 */
export const getFittedSectionChartHeight = (rowCount: number, availableHeight: number): number =>
	Math.max(getSectionChartHeight(rowCount), availableHeight || 0);

const getSectionBarThickness = (canvasHeight: number, rowCount: number): number => {
	if (rowCount <= 0) return SECTION_BAR_MAX_THICKNESS_PX;
	const plotHeight = Math.max(0, canvasHeight - CHART_PADDING[0] - CHART_PADDING[2]);
	const bandThickness = (plotHeight / rowCount) * DEFAULT_COLUMN_WIDTH_RATIO;
	return Math.max(1, Math.min(SECTION_BAR_MAX_THICKNESS_PX, bandThickness));
};

interface WalkthroughSectionScoreChartProps {
	rows: WalkthroughSectionRow[];
	height: number;
	fitContainerHeight?: boolean;
	unscored?: boolean;
}

export const WalkthroughSectionScoreChart: FC<WalkthroughSectionScoreChartProps> = ({
	rows,
	height,
	fitContainerHeight = false,
	unscored = false,
}) => {
	const [wrapperRef, { width: containerWidth, height: containerHeight }] = useElementSize();

	const canvasHeight = fitContainerHeight && containerHeight > 0 ? containerHeight : height;
	const barThickness = getSectionBarThickness(canvasHeight, rows.length);
	const data = useMemo(
		() =>
			rows.map((row) => ({
				...row,
				sectionLabel: row.groupTitle,
				plotValue: unscored
					? row.completionPercentage
					: row.percentageScore === null
					? 0
					: row.percentageScore,
			})),
		[rows, unscored]
	);

	const categoryOrder = useMemo(() => data.map((row) => row.sectionLabel).reverse(), [data]);

	const scale = useMemo(
		() => ({
			plotValue: { type: 'linear', min: 0, max: 100, nice: false, tickInterval: 20 },
			sectionLabel: { type: 'cat', values: categoryOrder },
		}),
		[categoryOrder]
	);

	const barColor: string | [string, (bucket: string) => string] = unscored
		? UNSCORED_BAR_COLOR
		: ['bucket', (bucket) => SECTION_COMPARISON_BUCKET_COLORS[bucket] || AXIS_LINE_COLOR];

	const renderTooltip = (title, items) => {
		const row = items && items.length > 0 ? items[0].data : null;
		if (!row) return '';

		const answeredRow = { label: 'Answered', value: `${row.answeredCount}/${row.totalCount}` };
		if (unscored) {
			return buildGroupedComparisonTooltipHtml([
				{
					swatchColor: UNSCORED_BAR_COLOR,
					heading: String(row.sectionLabel),
					rows: [answeredRow],
				},
			]);
		}

		const scoreValue =
			row.score === null
				? '--'
				: `${floatToPercentageString(
						row.percentageScore === null ? null : row.percentageScore / 100
				  )} (${row.score}/${row.maxPossibleScore})`;
		const averageValue =
			row.averagePercentageScore === null
				? 'Not available'
				: `${floatToPercentageString(row.averagePercentageScore / 100)} (${row.averageScore}/${
						row.averageMaxPossibleScore
				  })`;
		return buildGroupedComparisonTooltipHtml([
			{
				swatchColor: SECTION_COMPARISON_BUCKET_COLORS[row.bucket] || AXIS_LINE_COLOR,
				heading: String(row.sectionLabel),
				rows: [
					answeredRow,
					{ label: 'Score', value: scoreValue },
					{ label: 'Avg Score (Last 3)', value: averageValue },
				],
			},
		]);
	};

	const categoryAxisTitle = {
		text: `Sections (${rows.length})`,
		style: AXIS_TITLE_STYLE,
		autoRotate: true,
		offset: CATEGORY_AXIS_TITLE_OFFSET_PX,
	};

	return (
		<div
			ref={wrapperRef}
			className={cn(
				'analytics-g2-chart analytics-g2-chart--comparison w-full min-w-0',
				fitContainerHeight && 'flex items-center'
			)}
			style={{ height: fitContainerHeight ? '100%' : height }}
		>
			{containerWidth ? (
				<Chart
					width={containerWidth}
					height={canvasHeight}
					padding={CHART_PADDING}
					data={data}
					scale={scale}
					autoFit={false}
					theme="openwrench"
				>
					<Coordinate transpose={true} />
					<Axis
						name="sectionLabel"
						line={null}
						tickLine={null}
						title={categoryAxisTitle}
						label={{ formatter: truncateSectionLabel, style: AXIS_LABEL_STYLE }}
					/>
					<Axis
						name="plotValue"
						title={{
							text: unscored ? 'Answered' : 'Score',
							style: AXIS_TITLE_STYLE,
							offset: 36,
						}}
						label={{
							formatter: (value) => floatToPercentageString(Number(value) / 100, 0),
							style: AXIS_LABEL_STYLE,
						}}
						grid={{ line: { style: { stroke: AXIS_LINE_COLOR, lineDash: [4, 4] } } }}
					/>
					<Legend visible={false} />
					<Geom
						type="interval"
						position="sectionLabel*plotValue"
						color={barColor}
						size={barThickness}
					/>
					<Tooltip
						showTitle={false}
						shared={false}
						follow={true}
						showMarkers={false}
						showCrosshairs={true}
						crosshairs={CROSSHAIRS_CONFIG}
						customContent={renderTooltip}
					/>
				</Chart>
			) : null}
		</div>
	);
};

export default WalkthroughSectionScoreChart;
